import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { IOrbitWorkspace } from '../../orbitWorkspaces';
import { orbitTokens } from '../../styles';
import { OrbitWorkspaceTag } from '../../types';
import { PlainButton, type } from './pageStyles';

export type OrbitWorkspaceSubmenuSide = 'left' | 'right';

/*
  Figma 6690:3460: 126 wide and 172 tall for five names, #15203E at r8 with no shadow, 2 off the
  menu and top-aligned to the row that opens it. The names sit at x 10 from y 15, a 31 pitch, in
  the menu's own item type. A menu at the window's right edge opens it to the left.
*/
const Card = styled.div<{ $side: OrbitWorkspaceSubmenuSide; $top: number }>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  ${({ $side }) => ($side === 'right' ? 'left' : 'right')}: calc(100% + 2px);
  min-width: 126px;
  padding: 8px 0 9px;
  border-radius: 8px;
  background: #15203e;
  font-family: ${orbitTokens.fontFamily};
`;

// Hover and focus take the menu items' highlight.
const Item = styled(PlainButton)`
  display: flex;
  align-items: center;
  width: 100%;
  height: 31px;
  padding: 0 12px 0 10px;
  color: #aebbd0;
  white-space: nowrap;
  ${type('containerBody')}

  &:hover,
  &:focus-visible {
    outline: none;
    background: rgba(255, 255, 255, 0.06);
  }
`;

export interface IOrbitWorkspaceSubmenuProps {
  label: string;
  /** The Workspaces the chat can go to; the one it is in is left out. */
  options: readonly IOrbitWorkspace[];
  side: OrbitWorkspaceSubmenuSide;
  /** From the top of the menu, so the submenu lines up with the row that opened it. */
  top: number;
  onChoose: (tag: OrbitWorkspaceTag) => void;
  /** Escape, or the arrow pointing back at the menu: focus returns to the opening row. */
  onBack: () => void;
  /** Tab leaves the whole menu. */
  onLeave: () => void;
  testId: string;
}

/** "Add to" or "Move to Workspace ›" (6690:3458): a click files the chat there and closes the menu. */
const OrbitWorkspaceSubmenu: React.FC<IOrbitWorkspaceSubmenuProps> = ({
  label,
  options,
  side,
  top,
  onChoose,
  onBack,
  onLeave,
  testId
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    cardRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const back = side === 'right' ? 'ArrowLeft' : 'ArrowRight';
    if (event.key === 'Escape' || event.key === back || event.key === 'Tab') {
      event.preventDefault();
      // Kept from the menu's own handlers and rc-dropdown's window listener.
      event.stopPropagation();
      if (event.key === 'Tab') {
        onLeave();
      } else {
        onBack();
      }
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]')
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    items[(index + step + items.length) % items.length]?.focus();
  };

  return (
    <Card
      ref={cardRef}
      role="menu"
      aria-label={label}
      $side={side}
      $top={top}
      onKeyDown={onKeyDown}
      data-testid={testId}
    >
      {options.map(workspace => (
        <Item
          key={workspace.tag}
          type="button"
          role="menuitem"
          tabIndex={-1}
          onClick={() => onChoose(workspace.tag)}
        >
          {workspace.title}
        </Item>
      ))}
    </Card>
  );
};

export default OrbitWorkspaceSubmenu;
